import { useState } from 'react';
import { Shield, ShieldCheck, ShieldAlert, Unlock, RefreshCw, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient, formatApiErrorMessage } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface IpBanCheckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface BanCheckResult {
  ip: string;
  banned: boolean;
  jails: string[];
}

interface UnbanResult {
  ip: string;
  unbanned: boolean;
  partial?: boolean;
  jails: string[];
  failedJails?: string[];
  whitelisted: boolean;
  errors?: string[];
}

export function IpBanCheckDialog({ open, onOpenChange }: IpBanCheckDialogProps) {
  const [loading, setLoading] = useState(false);
  const [unbanning, setUnbanning] = useState(false);
  const [result, setResult] = useState<BanCheckResult | null>(null);
  const [unbanResult, setUnbanResult] = useState<UnbanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const checkIp = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setUnbanResult(null);

    try {
      const response = await apiClient.get<BanCheckResult>('/security/ip-ban-check');
      if (response?.success && response?.data) {
        setResult(response.data);
      } else {
        setError('Risposta non valida dal server');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nella verifica');
    } finally {
      setLoading(false);
    }
  };

  const handleUnban = async (whitelist: boolean) => {
    if (!result) return;

    setUnbanning(true);
    try {
      const response = await apiClient.post<UnbanResult>('/security/ip-unban', {
        ip: result.ip,
        whitelist
      });

      if (response?.success && response?.data) {
        setUnbanResult(response.data);
        setResult(null);
        const data = response.data;
        if (data.partial) {
          toast({
            title: 'Sblocco parziale',
            description: `Alcune jail non sono state sbloccate. Controllare i dettagli.`,
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'IP sbloccato',
            description: whitelist
              ? `${result.ip} sbloccato e aggiunto alla whitelist`
              : `${result.ip} sbloccato con successo`,
          });
        }
      } else {
        toast({
          title: 'Errore',
          description: 'Impossibile sbloccare l\'IP',
          variant: 'destructive'
        });
      }
    } catch (err) {
      toast({
        title: 'Errore sblocco IP',
        description: formatApiErrorMessage(err, 'Errore durante lo sblocco'),
        variant: 'destructive'
      });
    } finally {
      setUnbanning(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setResult(null);
      setUnbanResult(null);
      setError(null);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Verifica IP Ban
          </DialogTitle>
          <DialogDescription>
            Controlla se il tuo IP pubblico è bloccato da Fail2Ban
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!result && !unbanResult && !loading && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Clicca per verificare se il tuo indirizzo IP è attualmente bannato su uno dei server.
              </p>
              <Button onClick={checkIp} disabled={loading}>
                <Shield className="h-4 w-4 mr-2" />
                Verifica il mio IP
              </Button>
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Controllo in corso su tutti i server...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <ShieldAlert className="h-6 w-6 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-700">{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={checkIp}>
                <RefreshCw className="h-3 w-3 mr-1" />
                Riprova
              </Button>
            </div>
          )}

          {result && !result.banned && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="h-8 w-8 text-green-600" />
                <div>
                  <h4 className="font-semibold text-green-800">IP libero</h4>
                  <p className="text-sm text-green-600">Il tuo IP non è bannato su nessun server.</p>
                </div>
              </div>
              <div className="mt-3 bg-green-100 rounded px-3 py-2">
                <span className="text-xs text-green-700">Il tuo IP: </span>
                <code className="text-sm font-mono font-bold text-green-900">{result.ip}</code>
              </div>
              <div className="mt-3 flex justify-end">
                <Button variant="outline" size="sm" onClick={checkIp}>
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Ricontrolla
                </Button>
              </div>
            </div>
          )}

          {result && result.banned && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <ShieldAlert className="h-8 w-8 text-red-600" />
                <div>
                  <h4 className="font-semibold text-red-800">IP bannato</h4>
                  <p className="text-sm text-red-600">Il tuo IP è bloccato nelle seguenti jail:</p>
                </div>
              </div>
              <div className="mt-2 bg-red-100 rounded px-3 py-2">
                <span className="text-xs text-red-700">Il tuo IP: </span>
                <code className="text-sm font-mono font-bold text-red-900">{result.ip}</code>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {result.jails.map((jail) => (
                  <Badge key={jail} variant="destructive" className="text-xs">
                    {jail}
                  </Badge>
                ))}
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <Button
                  onClick={() => handleUnban(false)}
                  disabled={unbanning}
                  variant="default"
                  className="w-full"
                >
                  {unbanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlock className="h-4 w-4 mr-2" />}
                  Sblocca subito
                </Button>
                <Button
                  onClick={() => handleUnban(true)}
                  disabled={unbanning}
                  variant="outline"
                  className="w-full border-green-300 text-green-700 hover:bg-green-50"
                >
                  {unbanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Sblocca + Whitelist
                </Button>
              </div>
            </div>
          )}

          {unbanResult && (
            <div className={`${unbanResult.partial ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'} border rounded-lg p-4`}>
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className={`h-8 w-8 ${unbanResult.partial ? 'text-yellow-600' : 'text-green-600'}`} />
                <div>
                  <h4 className={`font-semibold ${unbanResult.partial ? 'text-yellow-800' : 'text-green-800'}`}>
                    {unbanResult.partial ? 'Sblocco parziale' : 'IP sbloccato!'}
                  </h4>
                  <p className={`text-sm ${unbanResult.partial ? 'text-yellow-600' : 'text-green-600'}`}>
                    {unbanResult.partial
                      ? 'Alcune jail non sono state sbloccate correttamente.'
                      : unbanResult.whitelisted
                        ? 'IP sbloccato e aggiunto alla whitelist permanente.'
                        : 'IP sbloccato con successo da tutte le jail.'}
                  </p>
                </div>
              </div>
              <div className="mt-2 bg-green-100 rounded px-3 py-2">
                <code className="text-sm font-mono font-bold text-green-900">{unbanResult.ip}</code>
              </div>
              {unbanResult.jails.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {unbanResult.jails.map((jail) => (
                    <Badge key={jail} variant="outline" className="text-xs text-green-700 border-green-300">
                      {jail}
                    </Badge>
                  ))}
                </div>
              )}
              {unbanResult.failedJails && unbanResult.failedJails.length > 0 && (
                <div className="mt-2">
                  <span className="text-xs text-red-600">Jail non sbloccate:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {unbanResult.failedJails.map((jail) => (
                      <Badge key={jail} variant="destructive" className="text-xs">
                        {jail}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <p className={`text-xs ${unbanResult.partial ? 'text-yellow-600' : 'text-green-600'} mt-3`}>
                Ricarica la pagina per riconnetterti al WebSocket SIP.
              </p>
              <div className="mt-3 flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={checkIp}>
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Ricontrolla
                </Button>
                <Button size="sm" onClick={() => window.location.reload()}>
                  Ricarica pagina
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
